import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  IconButton,
  Button,
  Card,
  CardContent,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Person,
  Assignment,
  Phone,
  Delete,
  MarkEmailRead,
  FilterList,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { markAsRead, markAllAsRead } from '../store/slices/notificationsSlice';

const Notifications = () => {
  const dispatch = useDispatch();
  const { notifications, unreadCount } = useSelector((state) => state.notifications);
  const [tabValue, setTabValue] = useState(0);
  const [filteredNotifications, setFilteredNotifications] = useState([]);

  useEffect(() => {
    filterNotifications();
  }, [notifications, tabValue]);

  const filterNotifications = () => {
    let filtered = [...notifications];
    
    switch (tabValue) {
      case 1: // Unread
        filtered = filtered.filter(n => !n.is_read);
        break;
      case 2: // Lead Related
        filtered = filtered.filter(n => 
          n.type === 'new_lead' || 
          n.type === 'lead_assigned' || 
          n.type === 'lead_status_changed'
        );
        break;
      case 3: // Calls
        filtered = filtered.filter(n => 
          n.type === 'call_scheduled' || 
          n.type === 'follow_up_reminder'
        );
        break;
      default:
        // All notifications
        break;
    }
    
    setFilteredNotifications(filtered);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'lead_assigned':
        return <Assignment color="primary" />;
      case 'new_lead':
        return <Person color="success" />;
      case 'call_scheduled':
        return <Phone color="info" />;
      default:
        return <NotificationsIcon color="info" />;
    }
  };

  const handleMarkAsRead = (notificationId) => {
    dispatch(markAsRead(notificationId));
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllAsRead());
  };

  const handleDeleteNotification = (notificationId) => {
    // Implement delete notification logic
    console.log('Delete notification:', notificationId);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Notifications
          {unreadCount > 0 && (
            <Chip 
              label={`${unreadCount} new`} 
              color="error" 
              size="small" 
              sx={{ ml: 2 }} 
            />
          )}
        </Typography>
        {unreadCount > 0 && (
          <Button
            startIcon={<MarkEmailRead />}
            onClick={handleMarkAllAsRead}
          >
            Mark All Read
          </Button>
        )}
      </Box>

      <Card>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" />
          <Tab 
            label={
              <Badge badgeContent={unreadCount} color="error">
                Unread
              </Badge>
            } 
          />
          <Tab label="Leads" />
          <Tab label="Calls" />
        </Tabs>

        <CardContent sx={{ p: 0 }}>
          {filteredNotifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No notifications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You're all caught up!
              </Typography>
            </Box>
          ) : (
            <List>
              {filteredNotifications.map((notification) => (
                <ListItem
                  key={notification.id}
                  sx={{
                    backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
                    borderLeft: notification.is_read ? 'none' : '4px solid',
                    borderLeftColor: 'primary.main',
                  }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteNotification(notification.id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar>
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="body1" 
                          fontWeight={notification.is_read ? 'normal' : 'bold'}
                        >
                          {notification.title}
                        </Typography>
                        {!notification.is_read && (
                          <Button
                            size="small"
                            onClick={() => handleMarkAsRead(notification.id)}
                          >
                            Mark Read
                          </Button>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(notification.created_at), 'MMM dd, yyyy HH:mm')}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Notifications;